import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Leaf, Award, Heart } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useSiteSettings } from '../hooks/useSiteSettings';
import ProductCarousel3D from '../components/ProductCarousel3D';
import { SeoHead } from '../components/SeoHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const ensureArray = (value) => Array.isArray(value) ? value : [];

export const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const { settings } = useSiteSettings();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const productsRes = await axios.get(`${API}/products?featured=true`);
        setFeaturedProducts(ensureArray(productsRes.data).slice(0, 8));
        const newsRes = await axios.get(`${API}/news`);
        setLatestNews(ensureArray(newsRes.data).slice(0, 2));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  };

  const values = [
    { icon: Leaf, title: settings.value1_title, description: settings.value1_text },
    { icon: Award, title: settings.value2_title, description: settings.value2_text },
    { icon: Heart, title: settings.value3_title, description: settings.value3_text },
  ];

  return (
    <div data-testid="home-page">
      <SeoHead
        title="Pomodorino Napoletano e Prodotti Tipici Campani"
        description="Le Bontà del Vesuvio: azienda agricola a Ercolano (NA) ai piedi del Vesuvio. Pomodorini napoletani, conserve artigianali e specialità campane. Stessa terra del Piennolo DOP. Ordina online."
        keywords="pomodorino napoletano, pomodoro del vesuvio, pomodoro del piennolo, prodotti tipici campani, conserve artigianali campane, azienda agricola ercolano, specialità vesuviane"
        canonical="/"
      />

      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-5.5rem)] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={settings.hero_image_url}
            alt="Paesaggio del Vesuvio"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="relative container-custom pt-14">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <span className="inline-block text-accent font-body text-sm uppercase tracking-widest mb-4">
              {settings.hero_badge}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-4">
              {settings.hero_title}
            </h1>
            <p className="font-body text-lg md:text-xl text-white/90 leading-relaxed mb-7 max-w-xl">
              {settings.hero_subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={`https://wa.me/${settings.whatsapp_number}?text=Ciao!%20Vorrei%20informazioni%20sui%20vostri%20prodotti.`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
              >
                <Button className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full px-8 py-6 text-base font-medium btn-primary">
                  Contattaci su WhatsApp
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/negozio" data-testid="hero-products-btn">
                <Button variant="outline" className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 hover:text-white rounded-full px-8 py-6 text-base font-medium">
                  Acquista Online
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 bg-white/70 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Products Showcase Section */}
      <section className="screen-section bg-background-paper relative grain-overlay overflow-hidden" data-testid="values-section">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-6">
            <span className="text-secondary font-body text-xs uppercase tracking-widest mb-2 block">
              Dal Campo alla Tavola — Terra Vulcanica del Vesuvio
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-text-DEFAULT mb-3">
              Pomodorini Napoletani e Specialità Campane
            </h2>
            <p className="font-body text-text-muted text-sm md:text-base">
              <strong>Pomodorini del Vesuvio</strong>, conserve artigianali e specialità tipiche campane
              — coltivati con cura ai piedi del Vesuvio, Ercolano (NA)
            </p>
          </motion.div>

          {/* Valori come chips compatte */}
          <motion.div
            {...fadeUp}
            className="flex flex-wrap justify-center gap-2 mb-7"
          >
            {values.map((value) => (
              <div
                key={value.title}
                className="flex items-center gap-2 bg-white border border-primary/10 rounded-full px-3.5 py-2 shadow-sm"
              >
                <div className="w-6 h-6 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <value.icon className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="font-heading text-sm font-semibold text-text-DEFAULT">{value.title}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Carousel full-width */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <ProductCarousel3D products={featuredProducts} />
        </motion.div>

        {/* CTA negozio */}
        <motion.div
          {...fadeUp}
          className="text-center mt-5"
        >
          <Link
            to="/negozio"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-semibold text-sm px-6 py-3 rounded-full transition-colors shadow-md hover:shadow-lg"
            data-testid="view-all-products-link"
          >
            <ArrowRight className="w-4 h-4" />
            Vai al negozio
          </Link>
          {featuredProducts.length > 0 && (
            <p className="font-body text-text-muted text-xs mt-2">
              {featuredProducts.length} prodotti in evidenza · scopri tutto il catalogo
            </p>
          )}
        </motion.div>
      </section>

      {/* About Preview */}
      <section className="screen-section bg-primary-dark text-white relative overflow-hidden" data-testid="about-preview-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[5/4] lg:aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src={settings.about_image_url}
                  alt="Le mani dei nostri agricoltori"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent rounded-2xl -z-10" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-accent font-body text-sm uppercase tracking-widest mb-4 block">
                {settings.about_label}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {settings.about_title}
              </h2>
              <p className="font-body text-white/80 text-base md:text-lg leading-relaxed mb-6">
                {settings.about_text}
              </p>
              <Link to="/chi-siamo" data-testid="about-cta-link">
                <Button className="bg-accent text-primary-dark hover:bg-accent-light rounded-full px-8 py-6 text-base font-medium btn-primary">
                  Scopri la nostra storia
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Latest News */}
      {latestNews.length > 0 && (
        <section className="screen-section" data-testid="latest-news-section">
          <div className="container-custom">
            <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between mb-8">
              <div>
                <span className="text-secondary font-body text-sm uppercase tracking-widest mb-4 block">
                  Novità dall'Azienda
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-text-DEFAULT">
                  Ultime notizie
                </h2>
              </div>
              <Link
                to="/news"
                className="mt-6 md:mt-0 inline-flex items-center text-primary font-body font-medium hover:text-primary-dark transition-colors"
              >
                Tutte le notizie
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {latestNews.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group"
                  data-testid={`news-card-${index}`}
                >
                  <Link to={`/news/${article.id}`} className="block">
                    <div className="relative aspect-[16/9] overflow-hidden rounded-xl mb-4">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover img-zoom"
                      />
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl font-semibold text-text-DEFAULT mb-3 group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    <p className="font-body text-text-muted leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* SEO Territory Section — visibile ai crawler, arricchisce il contesto semantico */}
      <section className="py-10 bg-white border-t border-primary/5" aria-label="Il territorio del Vesuvio e i nostri prodotti">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div {...fadeUp} className="text-center mb-8">
              <span className="text-secondary font-body text-xs uppercase tracking-widest mb-1 block">Il Territorio</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-text-DEFAULT">
                La Terra Vulcanica del Vesuvio
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div {...fadeUp}>
                <h3 className="font-heading text-lg font-semibold text-text-DEFAULT mb-3">
                  Pomodorino Napoletano — Eccellenza Campana
                </h3>
                <p className="font-body text-text-muted text-sm leading-relaxed">
                  I nostri <strong>pomodorini del Vesuvio</strong> crescono sulla stessa terra vulcanica
                  che ha reso celebre il <strong>Pomodoro del Piennolo del Vesuvio DOP</strong>.
                  Il suolo piroclastico, ricco di minerali e potassio, regala ai
                  <strong> pomodorini napoletani</strong> un equilibrio unico tra dolcezza e acidità:
                  il sapore autentico della <strong>tradizione agricola campana</strong>.
                </p>
              </motion.div>
              <motion.div {...fadeUp}>
                <h3 className="font-heading text-lg font-semibold text-text-DEFAULT mb-3">
                  Conserve Artigianali e Specialità Vesuviane
                </h3>
                <p className="font-body text-text-muted text-sm leading-relaxed">
                  Dalle <strong>conserve di pomodoro artigianali</strong> ai prodotti freschi di stagione:
                  ogni confezione porta con sé la storia e i profumi del Vesuvio. La nostra
                  <strong> azienda agricola a Ercolano (NA)</strong> porta avanti con orgoglio
                  le <strong>tradizioni gastronomiche della Campania</strong>, offrendo
                  <strong> prodotti tipici vesuviani</strong> di prima qualità direttamente a domicilio.
                </p>
              </motion.div>
            </div>

            <motion.div {...fadeUp} className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              {[
                { label: 'Pomodorino Napoletano', sub: 'Varietà vesuviane' },
                { label: 'Conserve Artigianali', sub: 'Ricetta tradizionale' },
                { label: 'Specialità Campane', sub: 'Territorio DOP' },
                { label: 'Terra Vulcanica', sub: 'Ai piedi del Vesuvio' },
              ].map((item) => (
                <div key={item.label} className="bg-background-paper rounded-xl p-3 border border-primary/10">
                  <p className="font-heading text-xs font-bold text-primary leading-tight">{item.label}</p>
                  <p className="font-body text-[10px] text-text-muted mt-0.5">{item.sub}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
        <section className="screen-section bg-background-paper" data-testid="cta-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-primary rounded-3xl p-10 md:p-16 text-center overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary-light/30 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/20 rounded-full blur-3xl" />
            
            <div className="relative">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                {settings.cta_title}
              </h2>
              <p className="font-body text-white/80 text-lg mb-10 max-w-2xl mx-auto">
                {settings.cta_text}
              </p>
              <a
                href={`https://wa.me/${settings.whatsapp_number}?text=Ciao!%20Vorrei%20informazioni%20sui%20vostri%20prodotti.`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-btn"
              >
                <Button className="bg-white text-primary hover:bg-background-paper rounded-full px-10 py-6 text-lg font-medium btn-primary">
                  Scrivici su WhatsApp
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
